import { useState, useMemo, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatPercent, formatNumber, cn } from '@/lib/utils'
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
  ScatterChart, Scatter, Cell,
} from 'recharts'
import { ArrowLeft, TrendingUp, TrendingDown, BarChart3, Activity, ChevronDown, ChevronRight, Download, Loader2, Brain, Users } from 'lucide-react'

const PERIODS = [
  { key: 'ytd', label: 'YTD' },
  { key: '1y', label: '1Y' },
  { key: '3y', label: '3Y' },
  { key: '5y', label: '5Y' },
  { key: '10y', label: '10Y' },
  { key: 'itd', label: 'ITD' },
] as const

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/* ── Reusable components ── */

function StatCard({ label, value, format = 'percent', positive }: {
  label: string; value: number | null | undefined; format?: 'percent' | 'number' | 'ratio'; positive?: boolean | null
}) {
  const formatted = value === null || value === undefined ? '-' :
    format === 'percent' ? formatPercent(value) :
    format === 'ratio' ? formatNumber(value, 2) + 'x' :
    formatNumber(value, 2)
  const colorClass = positive === null || positive === undefined ? '' :
    positive ? 'text-green-600' : 'text-red-600'
  return (
    <div className="bg-card border border-border rounded-lg p-3">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">{label}</div>
      <div className={cn('text-lg font-bold', colorClass)}>{formatted}</div>
    </div>
  )
}

function HeatmapCell({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined) return <td className="p-1 text-center text-xs text-muted-foreground">-</td>
  const pct = value * 100
  const intensity = Math.min(Math.abs(pct) / 5, 1)
  const bg = pct >= 0
    ? `rgba(34, 197, 94, ${intensity * 0.5})`
    : `rgba(239, 68, 68, ${intensity * 0.5})`
  return (
    <td className="p-1 text-center text-xs font-mono" style={{ backgroundColor: bg }}>
      {pct.toFixed(1)}%
    </td>
  )
}

function Section({ title, icon, children, defaultOpen = true }: {
  title: string; icon?: React.ReactNode; children: React.ReactNode; defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="bg-card border border-border rounded-lg">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 p-4 text-left hover:bg-muted/30 transition-colors"
      >
        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        {icon}
        <h2 className="text-lg font-semibold">{title}</h2>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  )
}

function MetricTable({ rows, columns }: {
  rows: { label: string; values: (string | number | null | undefined)[]; format?: 'percent' | 'number' | 'formula' }[];
  columns: string[];
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border">
          {columns.map(c => (
            <th key={c} className={cn('py-1.5 font-medium', c === columns[0] ? 'text-left' : 'text-right')}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-border/50 hover:bg-muted/50">
            {r.values.map((v, j) => (
              <td key={j} className={cn('py-1.5', j === 0 ? 'text-left' : 'text-right font-mono',
                j > 0 && r.format === 'formula' ? 'text-xs text-muted-foreground' : '')}>
                {v === null || v === undefined ? '-' :
                 typeof v === 'number' ? (r.format === 'percent' ? formatPercent(v) : formatNumber(v, 4)) : v}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Clamp extreme values in rolling series to prevent chart Y-axis blowup */
function clampSeries(data: { date: string; [k: string]: any }[], keys: string[], limit = 10): typeof data {
  return data.map(d => {
    const out = { ...d }
    for (const k of keys) {
      if (typeof out[k] === 'number' && (out[k] > limit || out[k] < -limit)) out[k] = null
    }
    return out
  })
}

/* ── Main Component ── */

export default function FundDetail() {
  const { fundId } = useParams<{ fundId: string }>()

  // Record the open so "Performance Analytics -> Most opened" reflects real attention.
  // Fire-and-forget: tracking must never block or break the page.
  useEffect(() => {
    if (!fundId) return
    api.post('/hf-analytics/opens', { fund_id: Number(fundId), source: 'fund-detail' })
      .catch(() => { /* tracking is best-effort */ })
  }, [fundId])
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const source = searchParams.get('source') || 'hfri'

  const [period, setPeriod] = useState<string>('itd')
  const [benchmarkId, setBenchmarkId] = useState<string | null>(null)
  const [benchmarkSource, setBenchmarkSource] = useState<string | null>(null)

  // Fetch benchmarks
  const { data: benchmarks } = useQuery({
    queryKey: ['benchmarks'],
    queryFn: () => api.get('/screening/benchmarks').then(r => r.data),
  })

  // Group benchmarks by category for optgroup display
  const benchmarkGroups = useMemo(() => {
    if (!benchmarks) return []
    const groups: Record<string, { id: string; name: string; source: string }[]> = {}
    for (const b of benchmarks) {
      const cat = b.category || 'Other'
      if (!groups[cat]) groups[cat] = []
      groups[cat].push({ id: b.id, name: b.name, source: b.source })
    }
    const order = ['Market Index', 'HFRI Index', 'HFR Index', 'Barclays Index',
      'LumRisk Index', 'Bloomberg Index', 'AltERS Index', 'Internal Fund']
    return order
      .filter(cat => groups[cat]?.length > 0)
      .map(cat => ({ category: cat, items: groups[cat] }))
  }, [benchmarks])

  // Fetch analysis
  const { data: analysis, isLoading, error } = useQuery({
    queryKey: ['fund-analysis', fundId, source, period, benchmarkId, benchmarkSource],
    queryFn: () => {
      const params = new URLSearchParams({ source, period })
      if (benchmarkId !== null && benchmarkSource) {
        params.set('benchmark_id', benchmarkId)
        params.set('benchmark_source', benchmarkSource)
      }
      return api.get(`/screening/funds/${fundId}/analysis?${params}`).then(r => r.data)
    },
    enabled: !!fundId,
  })

  /* ── Derived chart data ── */

  const navChartData = useMemo(() => {
    if (!analysis) return []
    const fundNav = analysis.nav || []
    const benchNav = analysis.benchmark?.nav || []
    const benchMap = new Map(benchNav.map((p: any) => [p.date, p.value]))
    return fundNav.map((p: any) => ({
      date: p.date, fund: p.value, benchmark: benchMap.get(p.date) ?? null,
    }))
  }, [analysis])

  const drawdownChartData = useMemo(() => {
    if (!analysis) return []
    const fundDd = analysis.drawdown || []
    const benchDd = analysis.benchmark?.drawdown || []
    const benchMap = new Map(benchDd.map((p: any) => [p.date, p.value != null ? p.value * 100 : null]))
    return fundDd.map((p: any) => ({
      date: p.date,
      fund: p.value != null ? p.value * 100 : null,
      benchmark: benchMap.get(p.date) ?? null,
    }))
  }, [analysis])

  const monthlyReturnsData = useMemo(() => {
    if (!analysis) return []
    return (analysis.returns || []).slice(-36).map((p: any) => ({
      date: p.date?.substring(0, 7),
      return: p.value != null ? p.value * 100 : null,
    }))
  }, [analysis])

  const yearlyReturnsData = useMemo(() => {
    if (!analysis?.yearly_returns) return []
    const fundYearly = analysis.yearly_returns
    const benchYearly = analysis.benchmark?.yearly_returns || []
    const benchMap = new Map(benchYearly.map((p: any) => [p.year, p.value]))
    return fundYearly.map((p: any) => ({
      year: String(p.year),
      fund: p.value != null ? p.value * 100 : null,
      benchmark: benchMap.has(p.year) ? ((benchMap.get(p.year) as number) ?? 0) * 100 : null,
    }))
  }, [analysis])

  const rollingSharpeData = useMemo(() => {
    if (!analysis?.rolling) return []
    const s12 = analysis.rolling.sharpe_12m || []
    const s36 = analysis.rolling.sharpe_36m || []
    // Merge both series by date (use union of dates, not just 12m)
    const dateMap: Record<string, any> = {}
    s12.forEach((p: any) => { dateMap[p.date] = { date: p.date, sharpe_12m: p.value, sharpe_36m: null } })
    s36.forEach((p: any) => {
      if (dateMap[p.date]) dateMap[p.date].sharpe_36m = p.value
      else dateMap[p.date] = { date: p.date, sharpe_12m: null, sharpe_36m: p.value }
    })
    const merged = Object.values(dateMap).sort((a: any, b: any) => a.date.localeCompare(b.date))
    return clampSeries(merged, ['sharpe_12m', 'sharpe_36m'], 10)
  }, [analysis])

  const rollingSortinoData = useMemo(() => {
    if (!analysis?.rolling) return []
    const s12 = analysis.rolling.sortino_12m || []
    const s36 = analysis.rolling.sortino_36m || []
    const dateMap: Record<string, any> = {}
    s12.forEach((p: any) => { dateMap[p.date] = { date: p.date, sortino_12m: p.value, sortino_36m: null } })
    s36.forEach((p: any) => {
      if (dateMap[p.date]) dateMap[p.date].sortino_36m = p.value
      else dateMap[p.date] = { date: p.date, sortino_12m: null, sortino_36m: p.value }
    })
    const merged = Object.values(dateMap).sort((a: any, b: any) => a.date.localeCompare(b.date))
    return clampSeries(merged, ['sortino_12m', 'sortino_36m'], 20)
  }, [analysis])

  const rollingBetaData = useMemo(() => {
    if (!analysis?.benchmark?.rolling_beta_36m) return []
    return analysis.benchmark.rolling_beta_36m.map((p: any) => ({ date: p.date, beta: p.value }))
  }, [analysis])

  const rollingAlphaData = useMemo(() => {
    if (!analysis?.benchmark?.rolling_alpha_36m) return []
    return analysis.benchmark.rolling_alpha_36m.map((p: any) => ({ date: p.date, alpha: p.value }))
  }, [analysis])

  const rollingCorrData = useMemo(() => {
    if (!analysis?.benchmark?.rolling_correlation_36m) return []
    return analysis.benchmark.rolling_correlation_36m.map((p: any) => ({ date: p.date, correlation: p.value }))
  }, [analysis])

  const rollingDeltaData = useMemo(() => {
    if (!analysis?.benchmark?.greeks?.delta) return []
    return clampSeries(
      analysis.benchmark.greeks.delta.map((p: any) => ({ date: p.date, value: p.value })),
      ['value'], 10
    )
  }, [analysis])

  const rollingGammaData = useMemo(() => {
    if (!analysis?.benchmark?.greeks?.gamma) return []
    return clampSeries(
      analysis.benchmark.greeks.gamma.map((p: any) => ({ date: p.date, value: p.value })),
      ['value'], 100
    )
  }, [analysis])

  const rollingVegaData = useMemo(() => {
    if (!analysis?.benchmark?.greeks?.vega) return []
    return clampSeries(
      analysis.benchmark.greeks.vega.map((p: any) => ({ date: p.date, value: p.value })),
      ['value'], 20
    )
  }, [analysis])

  const rollingRhoData = useMemo(() => {
    if (!analysis?.benchmark?.greeks?.rho) return []
    return analysis.benchmark.greeks.rho.map((p: any) => ({ date: p.date, value: p.value }))
  }, [analysis])

  const heatmapYears = useMemo(() => {
    if (!analysis?.heatmap) return []
    return Object.keys(analysis.heatmap).sort((a, b) => Number(b) - Number(a))
  }, [analysis])

  const stats = analysis?.statistics || {}
  const fund = analysis?.fund || {}
  const bm = analysis?.benchmark

  const [exporting, setExporting] = useState(false)
  const [llmRequested, setLlmRequested] = useState(false)
  const [similarOpen, setSimilarOpen] = useState(false)

  // LLM Analysis query — only fires when button is clicked
  const { data: llmData, isLoading: llmLoading, error: llmError } = useQuery({
    queryKey: ['llm-analysis', fundId, source, period, benchmarkId, benchmarkSource],
    queryFn: () => {
      const params = new URLSearchParams({ source, period })
      if (benchmarkId !== null && benchmarkSource) {
        params.set('benchmark_id', benchmarkId)
        params.set('benchmark_source', benchmarkSource)
      }
      return api.get(`/screening/funds/${fundId}/llm-analysis?${params}`).then(r => r.data)
    },
    enabled: llmRequested && !!fundId,
  })

  // Similar Funds query — only fires when section is opened
  const { data: similarData, isLoading: similarLoading } = useQuery({
    queryKey: ['similar-funds', fundId, source],
    queryFn: () => api.get(`/screening/funds/${fundId}/similar?source=${source}&limit=10`).then(r => r.data),
    enabled: similarOpen && !!fundId,
  })

  function handleBenchmarkChange(val: string) {
    if (!val) { setBenchmarkId(null); setBenchmarkSource(null); return }
    const sepIdx = val.indexOf(':')
    const src = val.substring(0, sepIdx)
    const id = val.substring(sepIdx + 1)
    setBenchmarkSource(src)
    setBenchmarkId(id)
  }

  async function handleExport() {
    if (!fundId || exporting) return
    setExporting(true)
    try {
      const params = new URLSearchParams({ source, period })
      if (benchmarkId !== null && benchmarkSource) {
        params.set('benchmark_id', benchmarkId)
        params.set('benchmark_source', benchmarkSource)
      }
      const response = await api.get(`/screening/funds/${fundId}/export?${params}`, { responseType: 'blob' })
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${fund.name || `Fund_${fundId}`}_${period}_analysis.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setExporting(false)
    }
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Screening
        </button>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load fund analysis. The fund may not have returns data.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* ─── Header ─── */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="h-4 w-4" /> Back to Screening
          </button>
          <h1 className="text-2xl font-bold">{fund.name || `Fund ${fundId}`}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            {fund.firm && <span>{fund.firm}</span>}
            {fund.main_strategy && <span className="px-2 py-0.5 rounded bg-primary/10 text-primary text-xs">{fund.main_strategy}</span>}
            {fund.sub_strategy && <span className="px-2 py-0.5 rounded bg-muted text-xs">{fund.sub_strategy}</span>}
            <span className={cn('px-2 py-0.5 rounded text-xs font-medium', source === 'hfri' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800')}>
              {source === 'hfri' ? 'HFR' : 'INT'}
            </span>
          </div>
          {/* Albourne Ratings */}
          {analysis?.albourne_ratings && (
            <div className="flex items-center gap-2 mt-2">
              {(['overall', 'quantitative', 'qualitative', 'operational', 'esg'] as const).map(key => {
                const label = key === 'overall' ? 'Overall' : key === 'quantitative' ? 'Quant' : key === 'qualitative' ? 'Qual' : key === 'operational' ? 'Ops' : 'ESG'
                const rating = analysis.albourne_ratings?.[key]
                if (!rating) return null
                const color = rating === 'A' ? 'bg-green-500' : rating === 'B' ? 'bg-yellow-500' : rating === 'C' ? 'bg-orange-500' : 'bg-red-500'
                return (
                  <span key={key} className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span className={cn('w-2.5 h-2.5 rounded-full inline-block', color)} />
                    {label}: {rating}
                  </span>
                )
              })}
              {analysis.albourne_ratings?.status && (
                <span className="text-xs text-muted-foreground ml-1">({analysis.albourne_ratings.status})</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── Controls: Period + Benchmark ─── */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-1">
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className={cn('px-3 py-1.5 text-sm font-medium rounded-md border transition-colors',
                period === p.key ? 'bg-primary text-primary-foreground border-primary' : 'border-input hover:bg-accent')}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Benchmark:</label>
          <select
            value={benchmarkId !== null ? `${benchmarkSource}:${benchmarkId}` : ''}
            onChange={e => handleBenchmarkChange(e.target.value)}
            className="px-3 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring max-w-sm"
          >
            <option value="">None</option>
            {benchmarkGroups.map(g => (
              <optgroup key={g.category} label={g.category}>
                {g.items.map(b => (
                  <option key={`${b.source}:${b.id}`} value={`${b.source}:${b.id}`}>{b.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        {analysis?.period && (
          <span className="text-xs text-muted-foreground">
            {analysis.period.start} to {analysis.period.end} ({analysis.period.months} months)
          </span>
        )}
        <button
          onClick={handleExport}
          disabled={exporting || !analysis}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ml-auto',
            'border-input hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {exporting ? 'Exporting...' : 'Export Excel'}
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20"><div className="spinner" /></div>
      ) : analysis ? (
        <>
          {/* ─── Strategy Description ─── */}
          {fund.description && (
            <Section title="Strategy Description" defaultOpen={false}>
              <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {fund.description}
              </div>
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-muted-foreground">
                {fund.management_fee != null && <span>Mgmt Fee: {fund.management_fee}%</span>}
                {fund.performance_fee != null && <span>Perf Fee: {fund.performance_fee}%</span>}
                {fund.minimum_investment != null && <span>Min Investment: ${formatNumber(fund.minimum_investment, 0)}</span>}
                {fund.lockup_months != null && <span>Lockup: {fund.lockup_months} months</span>}
                {fund.redemption_notice_days != null && <span>Notice: {fund.redemption_notice_days} days</span>}
              </div>
            </Section>
          )}

          {/* ─── AI Analysis ─── */}
          <Section title="AI Analysis" icon={<Brain className="h-4 w-4" />} defaultOpen={false}>
            {!llmRequested ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <button
                  onClick={() => setLlmRequested(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <Brain className="h-4 w-4" />
                  Generate AI Analysis
                </button>
                <p className="text-xs text-muted-foreground">Uses LLM to analyze fund metrics and provide commentary</p>
              </div>
            ) : llmLoading ? (
              <div className="flex items-center justify-center gap-2 py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Generating analysis...</span>
              </div>
            ) : llmError ? (
              <p className="text-sm text-destructive py-2">Failed to generate analysis. Please try again.</p>
            ) : llmData?.error ? (
              <div className="py-3 text-sm text-muted-foreground">
                <p>{llmData.error}</p>
              </div>
            ) : llmData?.analysis ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                    <Brain className="h-3 w-3" /> AI Generated
                  </span>
                  {llmData.model && <span>via {llmData.model === 'openai' ? 'GPT-4o' : 'Claude'}</span>}
                </div>
                <div className="text-sm leading-relaxed whitespace-pre-line border-l-2 border-primary/30 pl-4">
                  {llmData.analysis}
                </div>
              </div>
            ) : null}
          </Section>

          {/* ─── Similar Funds (TF-IDF) ─── */}
          <div className="bg-card border border-border rounded-lg">
            <button
              onClick={() => setSimilarOpen(v => !v)}
              className="w-full flex items-center gap-2 p-4 text-left hover:bg-muted/30 transition-colors"
            >
              {similarOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <Users className="h-4 w-4" />
              <h2 className="text-lg font-semibold">Similar Funds</h2>
            </button>
            {similarOpen && (
              <div className="px-4 pb-4">
                {similarLoading ? (
                  <div className="flex items-center justify-center gap-2 py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Finding similar funds...</span>
                  </div>
                ) : similarData?.note ? (
                  <p className="text-sm text-muted-foreground py-2">{similarData.note}</p>
                ) : similarData?.similar?.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-1.5 text-left font-medium">Fund Name</th>
                        <th className="py-1.5 text-left font-medium">Firm</th>
                        <th className="py-1.5 text-left font-medium">Strategy</th>
                        <th className="py-1.5 text-right font-medium">Similarity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {similarData.similar.map((f: any) => (
                        <tr key={f.fund_id} className="border-b border-border/50 hover:bg-muted/50 cursor-pointer"
                            onClick={() => navigate(`/dashboard/screening/${f.fund_id}?source=${f.source}`)}>
                          <td className="py-1.5 text-left text-primary hover:underline">{f.name}</td>
                          <td className="py-1.5 text-left text-muted-foreground">{f.firm || '-'}</td>
                          <td className="py-1.5 text-left text-muted-foreground text-xs">{f.main_strategy}{f.sub_strategy ? ` / ${f.sub_strategy}` : ''}</td>
                          <td className="py-1.5 text-right font-mono">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full" style={{ width: `${Math.round(f.similarity * 100)}%` }} />
                              </div>
                              <span>{(f.similarity * 100).toFixed(1)}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-muted-foreground py-2">No similar funds found.</p>
                )}
              </div>
            )}
          </div>

          {/* ─── Key Metrics Cards ─── */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            <StatCard label="Ann. Return" value={stats.ann_return} positive={stats.ann_return > 0} />
            <StatCard label="Volatility" value={stats.ann_volatility} />
            <StatCard label="Sharpe" value={stats.sharpe_ratio} format="number" positive={stats.sharpe_ratio > 0} />
            <StatCard label="Sortino" value={stats.sortino_ratio} format="number" positive={stats.sortino_ratio > 0} />
            <StatCard label="Max DD" value={stats.max_drawdown} positive={false} />
            <StatCard label="Calmar" value={stats.calmar_ratio} format="number" positive={stats.calmar_ratio != null && stats.calmar_ratio > 0} />
            <StatCard label="Win Rate" value={stats.win_rate} positive={stats.win_rate > 0.5} />
          </div>

          {/* ─── NAV Chart ─── */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" /> NAV History (base 100)
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={navChartData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(val: number) => [val?.toFixed(2), '']} labelFormatter={l => `Date: ${l}`} />
                <Legend />
                <Line type="monotone" dataKey="fund" name={fund.name || 'Fund'} stroke="#3b82f6" dot={false} strokeWidth={2} />
                {bm && <Line type="monotone" dataKey="benchmark" name={bm.name} stroke="#9ca3af" dot={false} strokeWidth={1.5} strokeDasharray="5 5" />}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* ─── Drawdown Chart ─── */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <TrendingDown className="h-5 w-5" /> Drawdown
            </h2>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={drawdownChartData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={(val: number, name: string) => [`${val?.toFixed(2)}%`, name === 'fund' ? 'Fund DD' : 'Bench DD']} labelFormatter={l => `Date: ${l}`} />
                <ReferenceLine y={0} stroke="#666" />
                <Area type="monotone" dataKey="fund" name="Fund" stroke="#ef4444" fill="#ef4444" fillOpacity={0.2} dot={false} />
                {bm && <Area type="monotone" dataKey="benchmark" name={bm.name} stroke="#9ca3af" fill="#9ca3af" fillOpacity={0.1} dot={false} />}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* ─── Crisis Events Timeline ─── */}
          {analysis.crisis_events?.events?.length > 0 && drawdownChartData.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-3">Crisis Events Timeline</h2>
              {(() => {
                const events = analysis.crisis_events.events as { name: string; start: string; end: string; category: string; color: string }[]
                const categories = analysis.crisis_events.categories as string[]
                const colors = analysis.crisis_events.colors as Record<string, string>
                const dates = drawdownChartData.map((d: any) => new Date(d.date).getTime())
                const minDate = Math.min(...dates)
                const maxDate = Math.max(...dates)
                const range = maxDate - minDate || 1
                const rowH = 18
                const padTop = 4
                const padBot = 4
                const svgH = padTop + categories.length * rowH + padBot
                const labelW = 120
                return (
                  <div className="flex">
                    <div className="shrink-0" style={{ width: labelW }}>
                      <svg width={labelW} height={svgH}>
                        {categories.map((cat, i) => (
                          <text key={cat} x={labelW - 4} y={padTop + i * rowH + rowH / 2 + 4}
                            textAnchor="end" fontSize={9} fill="currentColor" className="fill-muted-foreground">
                            {cat}
                          </text>
                        ))}
                      </svg>
                    </div>
                    <div className="flex-1 overflow-x-hidden">
                      <svg width="100%" height={svgH} preserveAspectRatio="none" viewBox={`0 0 1000 ${svgH}`}>
                        {categories.map((_, i) => (
                          <line key={i} x1={0} x2={1000} y1={padTop + i * rowH + rowH / 2}
                            y2={padTop + i * rowH + rowH / 2} stroke="currentColor" strokeOpacity={0.07} />
                        ))}
                        {events.map((ev, idx) => {
                          const catIdx = categories.indexOf(ev.category)
                          if (catIdx < 0) return null
                          const s = Math.max(new Date(ev.start).getTime(), minDate)
                          const e = Math.min(new Date(ev.end).getTime(), maxDate)
                          const x = ((s - minDate) / range) * 1000
                          const w = Math.max(((e - s) / range) * 1000, 3)
                          const y = padTop + catIdx * rowH + 2
                          return (
                            <g key={idx}>
                              <title>{ev.name} ({ev.start} — {ev.end})</title>
                              <rect x={x} y={y} width={w} height={rowH - 4}
                                fill={ev.color || colors[ev.category] || '#888'} rx={2} opacity={0.75} />
                            </g>
                          )
                        })}
                      </svg>
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {/* ─── Statistics + Worst Drawdowns (2-col) ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Activity className="h-5 w-5" /> Performance Metrics
                {analysis?.risk_free_rate != null && analysis.risk_free_rate !== 0 && (
                  <span className="text-xs text-muted-foreground font-normal ml-auto">Rf = {(analysis.risk_free_rate * 100).toFixed(2)}%/mo</span>
                )}
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-1 font-medium">Metric</th>
                    <th className="text-right py-1 font-medium">Fund</th>
                    {bm && <th className="text-right py-1 font-medium">{bm.name}</th>}
                  </tr>
                </thead>
                <tbody>
                  {([
                    ['Ann. Return', 'ann_return', 'p'],
                    ['Ann. Volatility', 'ann_volatility', 'p'],
                    ['Sharpe Ratio', 'sharpe_ratio', 'n'],
                    ['Sortino Ratio', 'sortino_ratio', 'n'],
                    ['Calmar Ratio', 'calmar_ratio', 'n'],
                    ['Omega Ratio', 'omega_ratio', 'n'],
                    ['Max Drawdown', 'max_drawdown', 'p'],
                    ['Current Drawdown', 'current_drawdown', 'p'],
                    ['Recovery (days)', 'recovery_days', 'i'],
                    ['VaR (95%)', 'var_95', 'p'],
                    ['CVaR (95%)', 'cvar_95', 'p'],
                    ['CF-CVaR (95%)', 'cvar_95_cf', 'p'],
                    ['Skewness', 'skewness', 'n'],
                    ['Kurtosis', 'kurtosis', 'n'],
                    ['Win Rate', 'win_rate', 'p'],
                    ['Best Month', 'best_month', 'p'],
                    ['Worst Month', 'worst_month', 'p'],
                    ['Ulcer Index', 'ulcer_index', 'n'],
                    ['Ulcer Ratio', 'ulcer_ratio', 'n'],
                    ['Autocorrelation', 'autocorrelation', 'n'],
                    ['Adj. Sharpe (Lo)', 'adjusted_sharpe', 'n'],
                  ] as [string, string, string][]).map(([label, key, fmt]) => {
                    const fmtFn = (v: any) => v === null || v === undefined ? '-' :
                      fmt === 'p' ? formatPercent(v) :
                      fmt === 'i' ? String(Math.round(v)) :
                      formatNumber(v, 3)
                    return (
                      <tr key={key} className="border-b border-border/50 hover:bg-muted/50">
                        <td className="py-1">{label}</td>
                        <td className="text-right font-mono">{fmtFn(stats[key])}</td>
                        {bm && <td className="text-right font-mono text-muted-foreground">{fmtFn(bm.statistics?.[key])}</td>}
                      </tr>
                    )
                  })}
                  {/* Robust Sharpe row */}
                  {analysis?.robust_sharpe && (
                    <tr className="border-b border-border/50 hover:bg-muted/50">
                      <td className="py-1">
                        Robust Sharpe
                        {analysis.robust_sharpe.significant_5pct != null && (
                          <span className={cn(
                            'ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold',
                            analysis.robust_sharpe.significant_5pct ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          )}>
                            {analysis.robust_sharpe.significant_5pct ? 'Sig' : 'N.S.'}
                          </span>
                        )}
                      </td>
                      <td className="text-right font-mono">
                        {analysis.robust_sharpe.sharpe != null ? formatNumber(analysis.robust_sharpe.sharpe, 3) : '-'}
                      </td>
                      {bm && <td className="text-right font-mono text-muted-foreground">-</td>}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="space-y-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="text-lg font-semibold mb-3">Worst Drawdowns</h2>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-1">#</th>
                      <th className="text-left py-1">Start</th>
                      <th className="text-left py-1">Trough</th>
                      <th className="text-left py-1">End</th>
                      <th className="text-right py-1">Max DD</th>
                      <th className="text-right py-1">Months</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analysis.worst_drawdowns || []).map((dd: any, i: number) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-1">{i + 1}</td>
                        <td className="py-1 font-mono text-xs">{dd.start?.substring(0, 7)}</td>
                        <td className="py-1 font-mono text-xs">{dd.trough?.substring(0, 7)}</td>
                        <td className="py-1 font-mono text-xs">{dd.end?.substring(0, 7) || 'Ongoing'}</td>
                        <td className="py-1 text-right font-mono text-red-600">{formatPercent(dd.max_dd)}</td>
                        <td className="py-1 text-right font-mono">{dd.duration_months}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {bm?.relative && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h2 className="text-lg font-semibold mb-3">vs {bm.name}</h2>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    {([
                      ['Alpha (ann.)', bm.relative.alpha, 'p'],
                      ['Beta', bm.relative.beta, 'n'],
                      ['R\u00b2', bm.relative.r_squared, 'n'],
                      ['Correlation', bm.relative.correlation, 'n'],
                      ['Tracking Error', bm.relative.tracking_error, 'p'],
                      ['Info. Ratio', bm.relative.information_ratio, 'n'],
                    ] as [string, number, string][]).map(([label, val, fmt]) => (
                      <div key={label} className="flex justify-between border-b border-border/50 py-1">
                        <span className="text-muted-foreground">{label}</span>
                        <span className="font-mono">{fmt === 'p' ? formatPercent(val) : formatNumber(val, 3)}</span>
                      </div>
                    ))}
                  </div>
                  {bm.capture && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-3 pt-3 border-t border-border">
                      {([
                        ['Upside Capture', bm.capture.upside_capture, '%'],
                        ['Downside Capture', bm.capture.downside_capture, '%'],
                        ['Capture Ratio', bm.capture.capture_ratio, 'x'],
                        ['Up Participation', bm.capture.upside_participation, '%'],
                        ['Down Protection', bm.capture.downside_protection, '%'],
                      ] as [string, number, string][]).map(([label, val, suffix]) => (
                        <div key={label} className="flex justify-between border-b border-border/50 py-1">
                          <span className="text-muted-foreground">{label}</span>
                          <span className="font-mono">{formatNumber(val, 1)}{suffix}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {bm.win_rate && (
                    <div className="mt-3 pt-3 border-t border-border text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Win Rate vs Benchmark</span>
                        <span className="font-mono font-bold">{formatPercent(bm.win_rate.rate)}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Fund outperforms {bm.win_rate.wins} of {bm.win_rate.total} months
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ─── Bayesian Estimates + Ruin Probability (2-col) ─── */}
          {(analysis.bayesian || (analysis.ruin_probability && analysis.ruin_probability.length > 0)) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {analysis.bayesian && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h2 className="text-lg font-semibold mb-3">Bayesian Estimates</h2>
                  <p className="text-xs text-muted-foreground mb-2">
                    Normal-Inverse-Gamma conjugate prior ({analysis.bayesian.n_observations} obs, {analysis.bayesian.n_samples?.toLocaleString()} samples)
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-1 font-medium">Metric</th>
                        <th className="text-right py-1 font-medium">5th</th>
                        <th className="text-right py-1 font-medium">25th</th>
                        <th className="text-right py-1 font-medium">Median</th>
                        <th className="text-right py-1 font-medium">75th</th>
                        <th className="text-right py-1 font-medium">95th</th>
                      </tr>
                    </thead>
                    <tbody>
                      {([
                        ['Sharpe', analysis.bayesian.sharpe, 'n'],
                        ['Ann. Return', analysis.bayesian.ann_return, 'p'],
                        ['Ann. Vol', analysis.bayesian.ann_volatility, 'p'],
                      ] as [string, any, string][]).map(([label, data, fmt]) => {
                        const fmtVal = (v: any) => v == null ? '-' : fmt === 'p' ? formatPercent(v) : formatNumber(v, 3)
                        return data ? (
                          <tr key={label} className="border-b border-border/50 hover:bg-muted/50">
                            <td className="py-1 font-medium">{label}</td>
                            <td className="text-right font-mono text-xs">{fmtVal(data.p5)}</td>
                            <td className="text-right font-mono text-xs">{fmtVal(data.p25)}</td>
                            <td className="text-right font-mono text-xs font-semibold">{fmtVal(data.median)}</td>
                            <td className="text-right font-mono text-xs">{fmtVal(data.p75)}</td>
                            <td className="text-right font-mono text-xs">{fmtVal(data.p95)}</td>
                          </tr>
                        ) : null
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {analysis.ruin_probability && analysis.ruin_probability.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h2 className="text-lg font-semibold mb-3">Ruin Probability</h2>
                  <p className="text-xs text-muted-foreground mb-2">
                    Probability of drawdown exceeding threshold
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-1 font-medium">Threshold</th>
                        <th className="text-right py-1 font-medium">Gaussian Est.</th>
                        <th className="text-right py-1 font-medium">Empirical</th>
                        <th className="text-right py-1 font-medium">Exceeded?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.ruin_probability.map((r: any) => (
                        <tr key={r.threshold} className="border-b border-border/50 hover:bg-muted/50">
                          <td className="py-1 font-mono">{formatPercent(r.threshold)}</td>
                          <td className="text-right font-mono text-xs">{r.gaussian_prob != null ? formatPercent(r.gaussian_prob) : '-'}</td>
                          <td className="text-right font-mono text-xs">{r.empirical_prob != null ? formatPercent(r.empirical_prob) : '-'}</td>
                          <td className="text-right">
                            <span className={`text-xs font-semibold ${r.exceeded ? 'text-red-600' : 'text-green-600'}`}>
                              {r.exceeded ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ─── Returns: Monthly + Yearly (2-col) ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <BarChart3 className="h-5 w-5" /> Monthly Returns (last 36m)
              </h2>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyReturnsData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={2} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                  <Tooltip formatter={(val: number) => [`${val?.toFixed(2)}%`, 'Return']} />
                  <ReferenceLine y={0} stroke="#666" />
                  <Bar dataKey="return" fill="#3b82f6" radius={[2, 2, 0, 0]}
                    shape={(props: any) => {
                      const { x, y, width, height, value } = props
                      const fill = value != null && value >= 0 ? '#22c55e' : '#ef4444'
                      return <rect x={x} y={y} width={width} height={Math.abs(height)} fill={fill} rx={2} />
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-3">Yearly Returns</h2>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={yearlyReturnsData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                  <Tooltip formatter={(val: number, name: string) => [`${val?.toFixed(2)}%`, name === 'fund' ? 'Fund' : 'Benchmark']} />
                  <Legend />
                  <ReferenceLine y={0} stroke="#666" />
                  <Bar dataKey="fund" name="Fund" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                  {bm && <Bar dataKey="benchmark" name={bm.name} fill="#9ca3af" radius={[2, 2, 0, 0]} />}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ─── Distribution + QQ-Plot (2-col) ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {analysis.distribution?.bins?.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="text-lg font-semibold mb-3">Return Distribution</h2>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analysis.distribution.bins}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="x" tick={{ fontSize: 10 }} tickFormatter={v => `${v.toFixed(1)}%`} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(val: number, _name: string) => [val, 'Count']}
                      labelFormatter={(l: number) => `${l.toFixed(1)}%`} />
                    {analysis.distribution.mean != null && (
                      <ReferenceLine x={analysis.distribution.mean} stroke="#3b82f6" strokeDasharray="5 5" label={{ value: 'Mean', position: 'top', fill: '#3b82f6', fontSize: 10 }} />
                    )}
                    <Bar dataKey="count" fill="#3b82f6" radius={[2, 2, 0, 0]}
                      shape={(props: any) => {
                        const { x, y, width, height } = props
                        const binX = props.x as number
                        const fill = binX >= (analysis.distribution.mean || 0) ? '#22c55e80' : '#ef444480'
                        return <rect x={x} y={y} width={width} height={Math.abs(height)} fill={fill} rx={1} />
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {analysis.qq_data?.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="text-lg font-semibold mb-3">QQ-Plot (vs Normal)</h2>
                <ResponsiveContainer width="100%" height={250}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="theoretical" name="Theoretical" tick={{ fontSize: 11 }} type="number"
                      label={{ value: 'Theoretical Quantiles', position: 'insideBottom', offset: -5, fontSize: 10 }} />
                    <YAxis dataKey="sample" name="Sample" tick={{ fontSize: 11 }} type="number"
                      label={{ value: 'Sample Quantiles', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                    <Tooltip formatter={(val: number) => [val?.toFixed(4), '']} />
                    <Scatter data={analysis.qq_data} fill="#3b82f6">
                      {analysis.qq_data.map((_: any, i: number) => (
                        <Cell key={i} fill="#3b82f6" />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ─── Monthly Returns Heatmap ─── */}
          {heatmapYears.length > 0 && (
            <Section title="Monthly Returns Heatmap" defaultOpen={true}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-1 px-1 font-medium">Year</th>
                      {MONTH_NAMES.map(m => <th key={m} className="text-center py-1 px-1 font-medium">{m}</th>)}
                      <th className="text-center py-1 px-1 font-bold">Year</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapYears.map(year => (
                      <tr key={year} className="border-b border-border/30">
                        <td className="py-1 px-1 font-medium">{year}</td>
                        {MONTH_NAMES.map(m => (
                          <HeatmapCell key={m} value={analysis.heatmap[year]?.[m]} />
                        ))}
                        <HeatmapCell value={analysis.heatmap[year]?.Year} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* ─── Rolling Sharpe + Sortino (2-col) ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {rollingSharpeData.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="text-lg font-semibold mb-3">Rolling Sharpe Ratio</h2>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={rollingSharpeData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(val: any) => [val != null ? Number(val).toFixed(3) : '-', '']} labelFormatter={l => l} />
                    <Legend />
                    <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="sharpe_12m" name="12m" stroke="#3b82f6" dot={false} strokeWidth={1.5} connectNulls />
                    <Line type="monotone" dataKey="sharpe_36m" name="36m" stroke="#f59e0b" dot={false} strokeWidth={1.5} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {rollingSortinoData.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="text-lg font-semibold mb-3">Rolling Sortino Ratio</h2>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={rollingSortinoData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(val: any) => [val != null ? Number(val).toFixed(3) : '-', '']} labelFormatter={l => l} />
                    <Legend />
                    <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="sortino_12m" name="12m" stroke="#22c55e" dot={false} strokeWidth={1.5} connectNulls />
                    <Line type="monotone" dataKey="sortino_36m" name="36m" stroke="#a855f7" dot={false} strokeWidth={1.5} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ─── Benchmark-dependent sections ─── */}
          {bm && (
            <>
              {/* HF vs Benchmark Returns Scatter with 3 regression lines */}
              {bm.scatter?.points?.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h2 className="text-lg font-semibold mb-3">HF vs Benchmark Returns</h2>
                  <div className="text-xs text-muted-foreground mb-2 flex flex-wrap gap-4">
                    {bm.scatter.regression && (
                      <span>
                        <span className="inline-block w-3 h-0.5 bg-blue-500 mr-1 align-middle" />
                        Unconditional: y = {bm.scatter.regression.slope.toFixed(3)}x + {bm.scatter.regression.intercept.toFixed(3)} | R² = {bm.scatter.regression.r_squared.toFixed(3)}
                      </span>
                    )}
                    {bm.scatter.upside_regression && (
                      <span>
                        <span className="inline-block w-3 h-0.5 bg-green-500 mr-1 align-middle" />
                        Upside Beta: {bm.scatter.upside_regression.slope.toFixed(3)}
                      </span>
                    )}
                    {bm.scatter.downside_regression && (
                      <span>
                        <span className="inline-block w-3 h-0.5 bg-red-500 mr-1 align-middle" />
                        Downside Beta: {bm.scatter.downside_regression.slope.toFixed(3)}
                      </span>
                    )}
                  </div>
                  <ResponsiveContainer width="100%" height={350}>
                    <ScatterChart>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="x" name={bm.name} type="number" tick={{ fontSize: 11 }}
                        label={{ value: `${bm.name} (%)`, position: 'insideBottom', offset: -5, fontSize: 10 }} />
                      <YAxis dataKey="y" name="Fund" type="number" tick={{ fontSize: 11 }}
                        label={{ value: 'Fund (%)', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                      <Tooltip formatter={(val: number) => [`${val?.toFixed(2)}%`, '']} />
                      <ReferenceLine y={0} stroke="#666" />
                      <ReferenceLine x={0} stroke="#666" />
                      {/* Unconditional regression (blue) */}
                      {bm.scatter.regression && (
                        <ReferenceLine
                          segment={[
                            { x: bm.scatter.regression.x_min, y: bm.scatter.regression.y_min },
                            { x: bm.scatter.regression.x_max, y: bm.scatter.regression.y_max },
                          ]}
                          stroke="#3b82f6" strokeWidth={2} />
                      )}
                      {/* Upside regression (green) */}
                      {bm.scatter.upside_regression && (
                        <ReferenceLine
                          segment={[
                            { x: bm.scatter.upside_regression.x_min, y: bm.scatter.upside_regression.y_min },
                            { x: bm.scatter.upside_regression.x_max, y: bm.scatter.upside_regression.y_max },
                          ]}
                          stroke="#22c55e" strokeDasharray="5 5" strokeWidth={2} />
                      )}
                      {/* Downside regression (red) */}
                      {bm.scatter.downside_regression && (
                        <ReferenceLine
                          segment={[
                            { x: bm.scatter.downside_regression.x_min, y: bm.scatter.downside_regression.y_min },
                            { x: bm.scatter.downside_regression.x_max, y: bm.scatter.downside_regression.y_max },
                          ]}
                          stroke="#ef4444" strokeDasharray="5 5" strokeWidth={2} />
                      )}
                      <Scatter data={bm.scatter.points} fill="#3b82f680">
                        {bm.scatter.points.map((_: any, i: number) => (
                          <Cell key={i} fill="#3b82f680" />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Quadratic Regression */}
              {bm.scatter?.quadratic?.curve?.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h2 className="text-lg font-semibold mb-3">Quadratic Regression</h2>
                  <div className="text-xs text-muted-foreground mb-2">
                    {(() => {
                      const c = bm.scatter.quadratic.coefficients
                      if (!c) return null
                      return (
                        <span>
                          y = {c[0].toFixed(4)}x² {c[1] >= 0 ? '+' : ''} {c[1].toFixed(4)}x {c[2] >= 0 ? '+' : ''} {c[2].toFixed(4)} | R² = {bm.scatter.quadratic.r_squared?.toFixed(4)}
                        </span>
                      )
                    })()}
                  </div>
                  <ResponsiveContainer width="100%" height={350}>
                    <ScatterChart>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="x" name={bm.name} type="number" tick={{ fontSize: 11 }}
                        label={{ value: `${bm.name} (%)`, position: 'insideBottom', offset: -5, fontSize: 10 }} />
                      <YAxis dataKey="y" name="Fund" type="number" tick={{ fontSize: 11 }}
                        label={{ value: 'Fund (%)', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                      <Tooltip formatter={(val: number) => [`${val?.toFixed(2)}%`, '']} />
                      <ReferenceLine y={0} stroke="#666" />
                      <ReferenceLine x={0} stroke="#666" />
                      {/* Scatter points */}
                      <Scatter data={bm.scatter.points} fill="#3b82f680" name="Returns">
                        {bm.scatter.points.map((_: any, i: number) => (
                          <Cell key={i} fill="#3b82f680" />
                        ))}
                      </Scatter>
                      {/* Quadratic curve as a line scatter */}
                      <Scatter data={bm.scatter.quadratic.curve} fill="none" line={{ stroke: '#ef4444', strokeWidth: 2.5 }}
                        name="Quadratic Fit" legendType="line">
                        {bm.scatter.quadratic.curve.map((_: any, i: number) => (
                          <Cell key={i} fill="transparent" r={0} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Rolling Beta + Alpha (2-col) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {rollingBetaData.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h2 className="text-lg font-semibold mb-3">Rolling Beta (36m)</h2>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={rollingBetaData}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(val: number) => [val?.toFixed(3), 'Beta']} labelFormatter={l => l} />
                        <ReferenceLine y={1} stroke="#666" strokeDasharray="3 3" />
                        <ReferenceLine y={0} stroke="#666" />
                        <Line type="monotone" dataKey="beta" name="Beta" stroke="#8b5cf6" dot={false} strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {rollingAlphaData.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h2 className="text-lg font-semibold mb-3">Rolling Alpha (36m, ann.)</h2>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={rollingAlphaData}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v * 100).toFixed(0)}%`} />
                        <Tooltip formatter={(val: number) => [formatPercent(val), 'Alpha']} labelFormatter={l => l} />
                        <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                        <Line type="monotone" dataKey="alpha" name="Alpha" stroke="#f59e0b" dot={false} strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Rolling Correlation */}
              {rollingCorrData.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h2 className="text-lg font-semibold mb-3">Rolling Correlation (36m) vs {bm.name}</h2>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={rollingCorrData}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} domain={[-1, 1]} tickFormatter={v => `${(v * 100).toFixed(0)}%`} />
                      <Tooltip formatter={(val: number) => [`${(val * 100).toFixed(1)}%`, 'Correlation']} labelFormatter={l => l} />
                      <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                      <Line type="monotone" dataKey="correlation" name="Correlation" stroke="#06b6d4" dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Rolling Greeks (2x2 grid) */}
              {(rollingDeltaData.length > 0 || rollingGammaData.length > 0 || rollingVegaData.length > 0 || rollingRhoData.length > 0) && (
                <Section title="Rolling Greeks (36m)" defaultOpen={false}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {rollingDeltaData.length > 0 && (
                      <div className="bg-muted/30 rounded-lg p-3">
                        <h3 className="text-sm font-semibold mb-2">Delta (Rolling Beta)</h3>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={rollingDeltaData}>
                            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(val: any) => [val != null ? Number(val).toFixed(3) : '-', 'Delta']} labelFormatter={l => l} />
                            <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                            <ReferenceLine y={1} stroke="#666" strokeDasharray="3 3" />
                            <Line type="monotone" dataKey="value" name="Delta" stroke="#3b82f6" dot={false} strokeWidth={1.5} connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    {rollingGammaData.length > 0 && (
                      <div className="bg-muted/30 rounded-lg p-3">
                        <h3 className="text-sm font-semibold mb-2">Gamma (Convexity)</h3>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={rollingGammaData}>
                            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(val: any) => [val != null ? Number(val).toFixed(4) : '-', 'Gamma']} labelFormatter={l => l} />
                            <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                            <Line type="monotone" dataKey="value" name="Gamma" stroke="#f97316" dot={false} strokeWidth={1.5} connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    {rollingVegaData.length > 0 && (
                      <div className="bg-muted/30 rounded-lg p-3">
                        <h3 className="text-sm font-semibold mb-2">Vega (Vol Sensitivity)</h3>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={rollingVegaData}>
                            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(val: any) => [val != null ? Number(val).toFixed(3) : '-', 'Vega']} labelFormatter={l => l} />
                            <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                            <Line type="monotone" dataKey="value" name="Vega" stroke="#22c55e" dot={false} strokeWidth={1.5} connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    {rollingRhoData.length > 0 && (
                      <div className="bg-muted/30 rounded-lg p-3">
                        <h3 className="text-sm font-semibold mb-2">Rho (Rolling Correlation)</h3>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={rollingRhoData}>
                            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 11 }} domain={[-1, 1]} />
                            <Tooltip formatter={(val: any) => [val != null ? Number(val).toFixed(3) : '-', 'Rho']} labelFormatter={l => l} />
                            <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                            <Line type="monotone" dataKey="value" name="Rho" stroke="#a855f7" dot={false} strokeWidth={1.5} connectNulls />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* Excess Return Metrics + Risk-Adjusted Excess (2-col) */}
              {(bm.excess_return_metrics?.length > 0 || bm.risk_adjusted_excess?.length > 0) && (
                <Section title="Excess Return Analysis" defaultOpen={true}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {bm.excess_return_metrics?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold mb-2">Excess Return Metrics</h3>
                        <MetricTable
                          columns={['Metric', 'Value', 'Formula']}
                          rows={bm.excess_return_metrics.map((m: any) => ({
                            label: m.metric,
                            values: [m.metric, m.value, m.formula],
                            format: m.metric.includes('Return') ? 'percent' as const : 'number' as const,
                          }))}
                        />
                      </div>
                    )}
                    {bm.risk_adjusted_excess?.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold mb-2">Risk-Adjusted Excess</h3>
                        <MetricTable
                          columns={['Metric', 'Value', 'Formula']}
                          rows={bm.risk_adjusted_excess.map((m: any) => ({
                            label: m.metric,
                            values: [m.metric, m.value, m.formula],
                            format: m.metric.includes('Tracking') || m.metric.includes('M2') ? 'percent' as const : 'number' as const,
                          }))}
                        />
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* Conditional Beta */}
              {bm.conditional_beta?.length > 0 && (
                <Section title="Conditional Beta Analysis" defaultOpen={false}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-1 font-medium">Condition</th>
                        <th className="text-right py-1 font-medium">Beta</th>
                        <th className="text-right py-1 font-medium">N</th>
                        <th className="text-left py-1 font-medium text-muted-foreground">Formula</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bm.conditional_beta.map((r: any, i: number) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1.5">{r.condition}</td>
                          <td className="py-1.5 text-right font-mono">{r.beta != null ? formatNumber(r.beta, 3) : '-'}</td>
                          <td className="py-1.5 text-right font-mono">{r.n}</td>
                          <td className="py-1.5 text-xs text-muted-foreground">{r.formula}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>
              )}

              {/* Tail Capture */}
              {bm.tail_capture?.length > 0 && (
                <Section title="Tail Capture Analysis" defaultOpen={false}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-1 font-medium">Condition</th>
                        <th className="text-right py-1 font-medium">Months</th>
                        <th className="text-right py-1 font-medium">Fund Avg</th>
                        <th className="text-right py-1 font-medium">Bench Avg</th>
                        <th className="text-right py-1 font-medium">Capture %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bm.tail_capture.map((r: any, i: number) => (
                        <tr key={i} className={cn('border-b border-border/50',
                          r.condition.startsWith('Worst') ? 'bg-red-50/50' : 'bg-green-50/50')}>
                          <td className="py-1.5">{r.condition}</td>
                          <td className="py-1.5 text-right font-mono">{r.months}</td>
                          <td className="py-1.5 text-right font-mono">{formatPercent(r.fund_avg)}</td>
                          <td className="py-1.5 text-right font-mono">{formatPercent(r.bench_avg)}</td>
                          <td className="py-1.5 text-right font-mono font-bold">{r.capture != null ? `${formatNumber(r.capture, 1)}%` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>
              )}

              {/* Higher Co-Moments */}
              {bm.higher_co_moments?.length > 0 && (
                <Section title="Higher Co-Moments (Tail Dependency)" defaultOpen={false}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-1 font-medium">Metric</th>
                        <th className="text-right py-1 font-medium">Value</th>
                        <th className="text-left py-1 font-medium text-muted-foreground">Formula</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bm.higher_co_moments.map((r: any, i: number) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-1.5">{r.metric}</td>
                          <td className="py-1.5 text-right font-mono">{r.value != null ? formatNumber(r.value, 4) : '-'}</td>
                          <td className="py-1.5 text-xs text-muted-foreground">{r.formula}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Section>
              )}
            </>
          )}

          {/* ─── Return vs S&P 500 TR & VIX ─── */}
          {analysis.market_scatter?.points?.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-3">Fund Returns vs S&P 500 TR {analysis.market_scatter.has_vix ? '& VIX Changes' : ''}</h2>
              <div className="text-xs text-muted-foreground mb-2">
                Each dot is a month. X = S&P 500 TR return, Y = Fund return{analysis.market_scatter.has_vix ? ', Color = VIX change (red = VIX up, green = VIX down)' : ''}.
              </div>
              <ResponsiveContainer width="100%" height={350}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="x" name="S&P 500 TR" type="number" tick={{ fontSize: 11 }}
                    label={{ value: 'S&P 500 TR (%)', position: 'insideBottom', offset: -5, fontSize: 10 }} />
                  <YAxis dataKey="y" name="Fund" type="number" tick={{ fontSize: 11 }}
                    label={{ value: 'Fund (%)', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                  <Tooltip
                    content={({ payload }: any) => {
                      if (!payload?.[0]?.payload) return null
                      const d = payload[0].payload
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg p-2 text-xs shadow-lg">
                          <div className="font-medium">{d.date}</div>
                          <div>S&P 500: {d.x?.toFixed(2)}%</div>
                          <div>Fund: {d.y?.toFixed(2)}%</div>
                          {d.vix != null && <div>VIX chg: {d.vix?.toFixed(1)}%</div>}
                        </div>
                      )
                    }}
                  />
                  <ReferenceLine y={0} stroke="#666" />
                  <ReferenceLine x={0} stroke="#666" />
                  <Scatter data={analysis.market_scatter.points} name="Returns">
                    {analysis.market_scatter.points.map((pt: any, i: number) => {
                      let fill = '#3b82f6'
                      if (analysis.market_scatter.has_vix && pt.vix != null) {
                        const intensity = Math.min(Math.abs(pt.vix) / 30, 1)
                        fill = pt.vix > 0
                          ? `rgba(239, 68, 68, ${0.3 + intensity * 0.6})`   // red = VIX up
                          : `rgba(34, 197, 94, ${0.3 + intensity * 0.6})`   // green = VIX down
                      }
                      return <Cell key={i} fill={fill} />
                    })}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
              {analysis.market_scatter.has_vix && (
                <div className="flex items-center justify-center gap-4 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-400" /> VIX rising</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-400" /> VIX falling</span>
                </div>
              )}
            </div>
          )}

          {/* ─── VIX Regime Analysis ─── */}
          {analysis.vix_regime?.length > 0 && (
            <Section title="VIX Regime Analysis" defaultOpen={true}>
              <div className="text-xs text-muted-foreground mb-3">
                Fund performance bucketed by VIX level at month-end. Helps identify how the fund behaves in different volatility environments.
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-1.5 font-medium">Regime</th>
                      <th className="text-right py-1.5 font-medium">Months</th>
                      <th className="text-right py-1.5 font-medium">Avg Return</th>
                      <th className="text-right py-1.5 font-medium">Volatility</th>
                      <th className="text-right py-1.5 font-medium">Sharpe</th>
                      <th className="text-right py-1.5 font-medium">Best</th>
                      <th className="text-right py-1.5 font-medium">Worst</th>
                      <th className="text-right py-1.5 font-medium">Win Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.vix_regime.map((r: any, i: number) => (
                      <tr key={i} className="border-b border-border/50 hover:bg-muted/50">
                        <td className="py-1.5 font-medium">{r.regime}</td>
                        <td className="py-1.5 text-right font-mono">{r.months}</td>
                        <td className={cn('py-1.5 text-right font-mono font-bold',
                          r.avg_return != null && r.avg_return >= 0 ? 'text-green-600' : 'text-red-600')}>
                          {r.avg_return != null ? formatPercent(r.avg_return) : '-'}
                        </td>
                        <td className="py-1.5 text-right font-mono">{r.volatility != null ? formatPercent(r.volatility) : '-'}</td>
                        <td className="py-1.5 text-right font-mono">{r.sharpe != null ? formatNumber(r.sharpe, 2) : '-'}</td>
                        <td className="py-1.5 text-right font-mono text-green-600">{r.best != null ? formatPercent(r.best) : '-'}</td>
                        <td className="py-1.5 text-right font-mono text-red-600">{r.worst != null ? formatPercent(r.worst) : '-'}</td>
                        <td className="py-1.5 text-right font-mono">{r.win_rate != null ? formatPercent(r.win_rate) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* ─── AUM History ─── */}
          {analysis.aum_history?.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-3">Fund AUM History</h2>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={analysis.aum_history}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.substring(0, 7)} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}B`} />
                  <Tooltip formatter={(val: number) => [`$${formatNumber(val, 0)}M`, 'AUM']} labelFormatter={l => l} />
                  <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ─── Fund Info Footer ─── */}
          <div className="bg-card border border-border rounded-lg p-4 text-sm text-muted-foreground">
            <div className="flex flex-wrap gap-6">
              {fund.inception_date && <span>Inception: {fund.inception_date}</span>}
              {fund.management_fee != null && <span>Mgmt Fee: {fund.management_fee}%</span>}
              {fund.performance_fee != null && <span>Perf Fee: {fund.performance_fee}%</span>}
              {fund.fund_aum != null && <span>Fund AUM: ${formatNumber(fund.fund_aum, 0)}M</span>}
              {fund.firm_aum != null && <span>Firm AUM: ${formatNumber(fund.firm_aum, 0)}M</span>}
              {fund.lockup_months != null && <span>Lockup: {fund.lockup_months}m</span>}
              {fund.redemption_notice_days != null && <span>Notice: {fund.redemption_notice_days}d</span>}
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
